import * as SelectPrimitive from '@radix-ui/react-select';
import { ForwardedRef, forwardRef, useContext } from 'react';
import styled, { css } from 'styled-components';
import { useText } from '../../../utils/hooks/use_text';
import { Icon, type IconType } from '../../icon';
import { theme as commonTheme } from '../common_input.theme';
import { CellulaInputSelectContext } from './_input_select.context';
import { theme } from './input_select.theme';

const elements = {
	trigger: styled(SelectPrimitive.SelectTrigger)<{ $variant: 'default' | 'ghost' }>`
		all: unset;
		display: inline-flex;
		align-items: center;
		gap: ${theme.getValue('triggerIconGap')};
		padding: ${commonTheme.getValue('basePaddingVertical')} ${commonTheme.getValue('basePaddingHorizontal')};
		background-color: ${commonTheme.getValue('baseBackground')};
		border: 1px solid ${commonTheme.getValue('baseBorderColor')};
		border-radius: ${commonTheme.getValue('baseBorderRadius')};
		color: ${theme.getValue('triggerColor')};
		transition: border-color 150ms;
		font: ${theme.getValue('itemCheckedFont')};
		background-color: ${theme.getValue('triggerSelectedBackground')};

		${({ $variant }) =>
			$variant === 'ghost' &&
			css`
				border: none;
				background-color: transparent;
				color: ${theme.getValue('itemIndicatorColor')};

				&:hover {
					background-color: ${theme.getValue('itemHighlightBackground')};
					border-color: transparent;
				}

				&:focus-visible {
					border-color: transparent;
					outline: 3px solid ${commonTheme.getValue('focusRingColor')};
				}

				&[data-placeholder] {
					background-color: transparent;
					color: ${theme.getValue('itemIndicatorColor')};
				}
			`}

		&:hover {
			border-color: ${commonTheme.getValue('hoverBorderColor')};
		}

		&:focus-visible {
			border-color: ${commonTheme.getValue('hoverBorderColor')};
			outline: 3px solid ${commonTheme.getValue('focusRingColor')};
		}

		&[data-disabled] {
			background-color: ${theme.getValue('itemDisabledBackground')};
			opacity: 0.7;
			pointer-events: none;
		}

		&[data-placeholder] {
			font: ${commonTheme.getValue('baseFont')};
			color: ${theme.getValue('triggerPlaceholderColor')};
			background-color: ${theme.getValue('triggerBackground')};
		}

		&[aria-invalid='true'] {
			border-color: ${theme.getValue('triggerInvalidBorder')};
			background-color: ${theme.getValue('triggerInvalidBackground')};
		}

		&[data-readonly] {
			opacity: 0.7;
			background: ${commonTheme.getValue('readOnlyBackground')};
			border-color: ${commonTheme.getValue('readOnlyBorderColor')};
		}
	`,
	arrowIcon: styled(SelectPrimitive.SelectIcon)<{ $variant: 'default' | 'ghost' }>`
		color: ${({ $variant }) => ($variant === 'ghost' ? theme.getValue('itemIndicatorColor') : theme.getValue('selectIconColor'))};
	`,
	leadingIcon: styled.span<{ $variant: 'default' | 'ghost' }>`
		display: flex;
		align-items: center;
		flex-shrink: 0;
		color: ${({ $variant }) => ($variant === 'ghost' ? theme.getValue('itemIndicatorColor') : theme.getValue('triggerIconColor'))};

		& > svg {
			width: ${theme.getValue('triggerIconSize')};
			height: ${theme.getValue('triggerIconSize')};
		}
	`,
	valueWrapper: styled.span`
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
	`,
};

export type Variant = 'default' | 'ghost';

export interface InputSelectTriggerProps extends SelectPrimitive.SelectTriggerProps {
	placeholder?: string;
	'aria-labelledby'?: string;
	'aria-describedby'?: string;
	'aria-label'?: string;
	icon?: IconType;
	variant?: Variant;
}

export const CellulaInputSelectTrigger = forwardRef(function CellulaInputSelectTrigger(
	{ placeholder, icon, variant = 'default', ...props }: InputSelectTriggerProps,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const staticTexts = useText();
	const context = useContext(CellulaInputSelectContext);
	const ariaLabelledby = props['aria-labelledby'];
	const ariaLabel = props['aria-label'];

	return (
		<elements.trigger
			aria-labelledby={ariaLabelledby || undefined}
			aria-label={ariaLabel || undefined}
			aria-invalid={context.invalid || undefined}
			{...props}
			data-readonly={context.readOnly || undefined}
			$variant={variant}
			ref={ref}
		>
			{icon && (
				<elements.leadingIcon aria-hidden='true' $variant={variant}>
					<Icon type={icon} />
				</elements.leadingIcon>
			)}
			<elements.valueWrapper>
				<SelectPrimitive.Value placeholder={placeholder ?? staticTexts('Component.InputSelect.Placeholder')} />
			</elements.valueWrapper>

			<elements.arrowIcon $variant={variant}>
				<Icon type='chevronDown' />
			</elements.arrowIcon>
		</elements.trigger>
	);
});
